import { computed, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  getProcessId,
  IBusinessPartner,
  IFeatureConfig,
  IGlobalConfig,
  ILoanProfile,
  IPartner,
  IPolicy,
  IProcessProfile,
  IProfileParams,
  Process
} from '@partners/api';
import { TCatalogOption } from '@partners/catalog';
import { IError } from '@partners/errors';
import { IFlatStage, IRoute, IStage } from '@partners/navigation';
import { IViewSettings, TFeature } from '@partners/settings';
import { TProcessId, TProcessMode } from '@partners/types';
import { Guid, isNotEmpty, takeOnce } from '@partners/utils';
import { combineLatest, filter, map, Observable } from 'rxjs';
import { StateAccessService } from '../../services';
import {
  IErrorsState,
  INavigationState,
  IShellState,
  IShellUiState,
  IShellViewState,
  ShellActions,
  ShellSelectors
} from '../store';

/**
 * @description
 *
 * this service serves as the base class for handling a feature's business logic, currently
 * it has two main functionalities:
 * 1. provide access to the feature's state data (see featureState() method)
 * 2. provide common http apis (load | submit)
 *
 */

@Injectable({
  providedIn: 'root'
})
export class ShellStore extends StateAccessService<IShellState> {
  readonly processId$$: Signal<TProcessId>;
  readonly processCode$$: Signal<number>;
  readonly processMode$$: Signal<TProcessMode>;
  readonly profile$$: Signal<IProcessProfile>;
  readonly token$$: Signal<string>;
  readonly progress$$: Signal<number>;
  readonly viewSettings$$: Signal<IViewSettings>;

  readonly navigation$: Observable<INavigationState>;
  readonly state$: Observable<IShellState>;
  readonly errors$: Observable<IErrorsState>;
  readonly errors$$: Signal<IErrorsState>;

  readonly uiState$: Observable<IShellUiState>;

  readonly params$: Observable<IProfileParams>;
  readonly params$$: Signal<IProfileParams>;
  readonly partner$: Observable<IPartner>;
  readonly policy$: Observable<IPolicy>;
  readonly policy$$: Signal<IPolicy>;
  readonly hasPolicy$$: Signal<boolean>;

  readonly loanReasons$$: Signal<TCatalogOption[]>;
  readonly loanProfiles$$: Signal<ILoanProfile[]>;
  readonly defaultLoanProfile$$: Signal<ILoanProfile>;

  readonly businessPartner$$: Signal<IBusinessPartner>;

  readonly globalConfig$$: Signal<IGlobalConfig>;
  readonly featuresConfig$$: Signal<IFeatureConfig[]>;

  readonly stages$: Observable<IStage[]>;

  readonly flatStages$: Observable<IFlatStage[]>;
  readonly anonymousStages$: Observable<Partial<IStage>[]>;
  readonly previousStage$: Observable<IStage>;
  readonly currentStage$: Observable<IStage>;
  readonly currentRoute$$: Signal<IRoute>;
  readonly nextStage$$: Signal<IStage>;

  readonly previousRoute$: Observable<IRoute>;
  readonly currentRoute$: Observable<IRoute>;
  readonly nextRoute$: Observable<IRoute>;

  readonly routeState$: Observable<{ previousRoute: IRoute; currentRoute: IRoute }>;

  readonly totalStages$$: Signal<number>;
  readonly totalCompleteStages$$: Signal<number>;
  readonly totalPristineStages$$: Signal<number>;
  readonly clientProcessId$$: Signal<string>;

  readonly matchCard$$: Signal<boolean>;
  readonly matchAcfl$$: Signal<boolean>;
  readonly matchBnpl$$: Signal<boolean>;
  readonly matchLoan$$: Signal<boolean>;

  constructor() {
    super();

    this.state$ = this.stateFacade.select$(ShellSelectors.shellState);
    this.navigation$ = this.stateFacade.select$<INavigationState>(ShellSelectors.navigation);
    this.errors$ = this.stateFacade.select$(ShellSelectors.errors);
    this.errors$$ = this.stateFacade.selectSignal$(ShellSelectors.errors);

    this.params$ = this.stateFacade.select$(ShellSelectors.params);
    this.partner$ = this.stateFacade.select$(ShellSelectors.profile).pipe(map(({ partner }) => partner));
    this.policy$ = this.stateFacade.select$(ShellSelectors.policy);

    this.businessPartner$$ = this.stateFacade.selectSignal$(ShellSelectors.businessPartner);

    this.uiState$ = this.stateFacade.select$(ShellSelectors.uiState);

    this.stages$ = this.stateFacade.select$(ShellSelectors.stages);

    this.flatStages$ = this.stateFacade.select$(ShellSelectors.flatStages);
    this.previousStage$ = this.stateFacade.select$(ShellSelectors.previousStage);
    this.currentStage$ = this.stateFacade.select$(ShellSelectors.currentStage);
    this.nextStage$$ = this.stateFacade.selectSignal$(ShellSelectors.nextStage);
    this.anonymousStages$ = this.stateFacade.select$(ShellSelectors.anonymousStages);

    this.previousRoute$ = this.stateFacade.select$(ShellSelectors.previousRoute);
    this.currentRoute$ = this.stateFacade.select$(ShellSelectors.currentRoute);
    this.nextRoute$ = this.stateFacade.select$(ShellSelectors.nextRoute);

    this.routeState$ = combineLatest([this.previousRoute$, this.currentRoute$]).pipe(
      map(([previousRoute, currentRoute]) => ({ previousRoute, currentRoute }))
    );

    this.totalCompleteStages$$ = this.stateFacade.selectSignal$(ShellSelectors.totalCompleteStages);
    this.totalPristineStages$$ = this.stateFacade.selectSignal$(ShellSelectors.totalPristineStages);
    this.clientProcessId$$ = this.stateFacade.selectSignal$(ShellSelectors.clientProcessId);

    this.totalStages$$ = computed(() => {
      return this.totalCompleteStages$$() + this.totalPristineStages$$();
    });
    this.currentRoute$$ = this.stateFacade.selectSignal$(ShellSelectors.currentRoute);
    this.params$$ = this.stateFacade.selectSignal$(ShellSelectors.params);
    this.token$$ = this.stateFacade.selectSignal$<string>(ShellSelectors.token);
    this.profile$$ = this.stateFacade.selectSignal$<IProcessProfile>(ShellSelectors.profile);
    this.processMode$$ = this.stateFacade.selectSignal$<TProcessMode>(ShellSelectors.mode);
    this.policy$$ = this.stateFacade.selectSignal$(ShellSelectors.policy);
    this.hasPolicy$$ = computed(() => {
      const policy = this.policy$$();
      return isNotEmpty(policy);
      // return isNotEmpty(policy?.opportunityId) && policy?.opportunityId != 0;
    });

    this.loanReasons$$ = this.stateFacade.selectSignal$(ShellSelectors.loanReasons);
    this.loanProfiles$$ = this.stateFacade.selectSignal$(ShellSelectors.loanProfiles);

    this.matchCard$$ = computed(() => this.matchProcess(Process.card));
    /** match Account Frame Loan (הלוואה ממסגרת החשבון === הלוואה ללא כרטיס) */
    this.matchAcfl$$ = computed(() => this.matchProcess(Process.acfl));
    /** match any By Now Pay Later loan  */
    this.matchBnpl$$ = computed(() => this.matchProcess(Process.bnpl));
    /** match any loan */
    this.matchLoan$$ = computed(() => this.matchAcfl$$() || this.matchBnpl$$());

    this.defaultLoanProfile$$ = computed(() => {
      const profiles = this.loanProfiles$$();

      if (profiles?.length) {
        return profiles[0];
      }
      return undefined;
    });

    this.globalConfig$$ = toSignal(this.stateFacade.select$(ShellSelectors.globalConfig));
    this.featuresConfig$$ = toSignal(this.stateFacade.select$(ShellSelectors.featuresConfig));

    this.progress$$ = toSignal(
      this.stateFacade.select$(ShellSelectors.uiState).pipe(map(({ progressValue }) => progressValue))
    );

    this.viewSettings$$ = toSignal(
      this.stateFacade.select$(ShellSelectors.uiState).pipe(
        filter((uiState) => !!uiState),
        map(({ isFooterFixed, isProgressHidden, isProgressRendered, headerCloseAction }) => ({
          isFooterFixed,
          isProgressHidden,
          headerCloseAction,
          isProgressRendered
        }))
      )
    );

    this.processId$$ = computed<TProcessId>(() => {
      const processCode = this.stateFacade.selectSignal$<number>(ShellSelectors.process);
      return getProcessId(processCode());
    });

    this.processCode$$ = computed<number>(() => {
      const processCode = this.policy$$()?.stages?.[0]?.processCode;
      return processCode as unknown as number;
    });
  }

  screenCode$$(screenName: string): Signal<number> {
    return toSignal(
      this.flatStages$.pipe(
        takeOnce(),
        map((stages) =>
          stages?.find((s) => s.url === `/${screenName}`)?.screenCode as unknown as number
        )
      )
    );
  }

  selectError$(token: Guid): Observable<IError> {
    return this.errors$.pipe(
      map((errorDictionary) => {
        if (!errorDictionary) {
          return undefined;
        }
        const errorByToken = errorDictionary[token];
        return errorByToken;
      })
    );
  }

  findFeatureConfig$$(featureName: TFeature): Signal<IFeatureConfig> {
    return computed(() => {
      const configArray = this.featuresConfig$$();
      return configArray?.find((m) => m.name === featureName);
    });
  }

  dispatchUpdateProfileParams(params: Partial<IProfileParams>) {
    this.stateFacade.dispatch(ShellActions.updateProfileParams({ params }));
  }

  dispatchShellReset() {
    this.stateFacade.dispatch(ShellActions.reset());
  }

  dispatchUpdateFeatureConfig(featureName: TFeature, featureConfig: IFeatureConfig) {
    this.stateFacade.dispatch(ShellActions.updateFeatureConfig({ featureName, featureConfig }));
  }

  dispatchSaveError(error: IError) {
    this.stateFacade.dispatch(ShellActions.saveAppError({ error }));
  }

  dispatchSaveClientProcessId(clientProcessId: string) {
    this.stateFacade.dispatch(ShellActions.saveClientProcessId({ clientProcessId: clientProcessId }));
  }

  dispatchUpdateProcessProfile(profile: IProcessProfile) {
    this.stateFacade.dispatch(ShellActions.updateProcessProfile({ profile }));
  }

  dispatchUpdateShellView(viewState: IShellViewState) {
    this.stateFacade.dispatch(ShellActions.updateShellView({ viewState }));
  }

  dispatchLoadPolicy() {
    this.stateFacade.dispatch(ShellActions.loadPolicy());
  }

  dispatchLoadPolicyFailure(error: any) {
    this.stateFacade.dispatch(ShellActions.loadPolicyFailure({ error }));
  }

  dispatchUpdateStages(stages: IStage[]) {
    this.stateFacade.dispatch(ShellActions.updateStages({ stages }));
  }

  dispatchUpdateAnonymousStages(stage: Partial<IStage>) {
    this.stateFacade.dispatch(ShellActions.updateAnonymousStage({ stage }));
  }

  dispatchCreateAnonymousStages(stages: Partial<IStage>[]) {
    this.stateFacade.dispatch(ShellActions.createAnonymousStage({ stages }));
  }

  dispatchUpdateFlatStages(stages: IFlatStage[]) {
    this.stateFacade.dispatch(ShellActions.updateFlatStages({ flatStages: stages }));
  }
  dispatchUpdateRouteNavigation(previousRoute?: IRoute, currentRoute?: IRoute) {
    this.stateFacade.dispatch(ShellActions.updateRouteNavigation({ previousRoute, currentRoute }));
  }
  dispatchUpdateStageNavigation(previousStage?: IStage, currentStage?: IStage, nextStage?: IStage) {
    this.stateFacade.dispatch(ShellActions.updateStageNavigation({ previousStage, currentStage, nextStage }));
  }

  private matchProcess = (processQuery: number): boolean => {
    // const processId = this._shellStore.processId$$();
    // return processId === processQuery;
    const profile = this.profile$$();
    return Number(profile?.params?.lobCode) === processQuery;
  };
}
